/**
 * SourcePicker — the live audio-source switcher. This is the owner's "auto-hiding dropdown": it
 * lives INSIDE the top-right menu (and the gallery header), so it inherits that panel's show/hide
 * with no timer of its own.
 *
 * Desktop (Tauri): lists native sources — System Audio, capture/input devices (Stereo Mix,
 * VB-Cable, a DJ interface) and running apps (Spotify, Chrome…) — and switches between them live
 * via captureNative(). Every native source is a passive tap, so picking one never blocks the
 * sound from reaching the speakers.
 *
 * Web: lists audio INPUT devices (the loopback-cable path) via captureInput(). Device labels need
 * a one-time permission, so we only probe when the user actually opens the control — never on load.
 *
 * A file button is always offered alongside (one file → loadFile; several → loadStems).
 */
import React, { useEffect, useRef, useState } from 'react'
import { captureInput, captureNative, currentSourceId, listInputs, loadFile, loadStems, onSource } from './audioSession'
import { audioPermission, isNativeAudio, listNativeSources, requestAudioPermission, type NativeSource } from './audio/desktop'

export function SourcePicker({ variant = 'menu' }: { variant?: 'menu' | 'bar' }): JSX.Element {
  const native = isNativeAudio()
  const fileRef = useRef<HTMLInputElement>(null)
  const [sources, setSources] = useState<NativeSource[]>([])
  const [inputs, setInputs] = useState<MediaDeviceInfo[] | null>(null)
  const [cur, setCur] = useState(currentSourceId())
  const [label, setLabel] = useState('')
  const [perm, setPerm] = useState<'granted' | 'needed' | 'n/a'>('n/a')
  const [asked, setAsked] = useState(false)

  // reflect whatever the session is actually playing (a file, another view's pick, …)
  useEffect(() => onSource((l) => { setLabel(l); setCur(currentSourceId()) }), [])
  // native sources are cheap + nearly static — list them up front so the dropdown is ready
  useEffect(() => { if (native) void listNativeSources().then(setSources) }, [native])
  // …and find out whether the OS will actually let us see them. Without this the list just looks
  // short and the user reasonably concludes we cannot find their music app.
  useEffect(() => { if (native) void audioPermission().then(setPerm) }, [native])

  // re-list when the dropdown opens: apps come and go; web input labels need a permission probe
  const refresh = (): void => {
    if (native) { void listNativeSources().then(setSources); void audioPermission().then(setPerm) }
    else if (inputs === null) void listInputs().then(setInputs)
  }

  // Ask once, then say where the toggle lives: macOS shows its prompt only the first time, and the
  // grant does not apply to a process that was already running — so "allow" alone looks like nothing
  // happened unless we tell the user to reopen.
  const askPermission = (): void => {
    setAsked(true)
    void requestAudioPermission().then((ok) => { setPerm(ok ? 'granted' : 'needed'); if (ok) refresh() })
  }

  const pick = (value: string): void => {
    if (!value) return
    if (native) {
      const s = sources.find((x) => x.id === value)
      void captureNative(value, s?.name ?? 'audio source')
    } else {
      const d = inputs?.find((x) => x.deviceId === value)
      void captureInput(value, d?.label || 'input device')
    }
  }

  const onFiles = (files: File[]): void => {
    if (files.length > 1) void loadStems(files)
    else if (files[0]) loadFile(files[0])
  }

  // is the live source one of our options? if not (a file, an unprobed input), show its label
  const known = native ? sources.some((s) => s.id === cur) : (inputs ?? []).some((d) => d.deviceId === cur)
  const placeholder = label || 'connect audio source'
  const devices = native ? sources.filter((s) => s.kind === 'device') : []
  const apps = native ? sources.filter((s) => s.kind === 'app') : []

  const selectStyle: React.CSSProperties = {
    background: '#15151f', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 6, padding: '4px 6px', font: '11px monospace',
    flex: variant === 'menu' ? '1 1 auto' : '0 0 auto',
    maxWidth: variant === 'bar' ? 220 : undefined, minWidth: 0
  }
  const fileBtn: React.CSSProperties = {
    background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.22)',
    borderRadius: 6, padding: '4px 9px', font: '11px monospace', cursor: 'pointer', flex: '0 0 auto'
  }

  // Shown only when the OS is actually withholding the app list — never in the way otherwise.
  const permissionHint = native && perm === 'needed' ? (
    <div style={{ font: '11px monospace', color: '#ffd479', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span>only “System Audio” until macOS lets us see individual apps.</span>
      <button type="button" onClick={askPermission} style={fileBtn}>
        {asked ? 'reopen SYQEL Art to finish' : 'allow screen recording'}
      </button>
      {asked && <span style={{ opacity: 0.75 }}>System Settings → Privacy &amp; Security → Screen Recording</span>}
    </div>
  ) : null

  const select = (
    <select value={known ? cur : ''} title="audio source" onMouseDown={refresh} onFocus={refresh} onChange={(e) => pick(e.target.value)} style={selectStyle}>
      <option value="" disabled>{placeholder}</option>
      {native ? (
        <>
          {sources.filter((s) => s.kind === 'system').map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          {devices.length > 0 && (
            <optgroup label="input devices">
              {devices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </optgroup>
          )}
          {apps.length > 0 && (
            <optgroup label="apps">
              {apps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </optgroup>
          )}
        </>
      ) : (
        (inputs ?? []).map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || `input ${d.deviceId.slice(0, 6)}…`}</option>)
      )}
    </select>
  )

  const file = (
    <>
      <input ref={fileRef} type="file" accept="audio/*,video/*" multiple style={{ display: 'none' }} onChange={(e) => onFiles(Array.from(e.target.files ?? []))} />
      <button title="open an audio or video file" style={fileBtn} onClick={() => fileRef.current?.click()}>📁</button>
    </>
  )

  if (variant === 'bar') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>{select}{file}</div>
        {permissionHint}
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ opacity: 0.7, letterSpacing: 0.5 }}>AUDIO SOURCE</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>{select}{file}</div>
      {permissionHint}
    </div>
  )
}
